//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * A suggested KPI score for a quarter, computed from what the quarter shows
 * (plan, delivery, deadlines, rework, utilisation, assessments). The owner's
 * rule (2026-10-09): scores stay manual — this only proposes a number and
 * says why; a Superadmin accepts it or writes their own (see finalKpi).
 *
 * Every component is a 0–1 figure with a plain formula; the weighted score
 * maps onto the company band: 0 → KPI_MIN (70), 1 → 100, 1.2 → KPI_MAX (120).
 * A component without data is skipped and its weight is shared out among
 * the others in proportion, so a missing number never counts as a zero.
 */

import { KPI_MAX, KPI_MIN, clampKpi } from './calc'

/** What a quarter shows for one employee. Counts are whole numbers ≥ 0. */
export interface QuarterMetrics {
  /** Issues assigned / planned for the quarter. */
  planned: number
  /** Issues finished in the quarter. */
  done: number
  /** Of the finished ones, how many met their due date. */
  onTime: number
  /** Finished issues that were reopened (rework). */
  reopened: number
  /** Working capacity of the quarter, in hours (sprint capacity). */
  capacityHours: number
  /** Hours actually logged; absent = not tracked. */
  loggedHours?: number
  /** The person's own assessment, on the KPI band 70–120; absent = not given. */
  selfAssessment?: number
  /** The lead's assessment, 70–120; absent = not given. */
  leadAssessment?: number
}

export type KpiComponentKey = 'delivery' | 'timeliness' | 'quality' | 'utilization' | 'assessment'

/** Percentages; they must add up to 100. */
export type KpiWeights = Record<KpiComponentKey, number>

export const DEFAULT_KPI_WEIGHTS: KpiWeights = {
  delivery: 40,
  timeliness: 25,
  quality: 15,
  utilization: 10,
  assessment: 10
}

export const KPI_COMPONENT_KEYS: readonly KpiComponentKey[] = [
  'delivery',
  'timeliness',
  'quality',
  'utilization',
  'assessment'
]

/** The 0–1 ceiling of the weighted score: delivery beyond plan can lift it to 1.2 (= KPI 120). */
export const KPI_SCORE_MAX = 1.2

export interface KpiComponent {
  key: KpiComponentKey
  /** The weight actually applied (after redistribution), in percent. */
  weight: number
  /** 0–1 (delivery up to 1.2), or null when the quarter has no data for it. */
  value: number | null
  /** weight/100 × value; 0 when skipped. */
  points: number
  /** One line, in Uzbek, saying what the number came from. */
  reason: string
}

export interface KpiSuggestion {
  /** 70–120, one decimal. */
  kpi: number
  /** The weighted 0–1.2 score the kpi was mapped from. */
  score: number
  components: KpiComponent[]
  /** One sentence a Superadmin can read next to the number. */
  basis: string
  /** false when fewer than two components had data — then the number is a guess. */
  confident: boolean
}

const EPS = 0.001

/** Weights are valid when they add up to 100 and none is negative. */
export function kpiWeightsAreValid (weights: KpiWeights): boolean {
  const vals = KPI_COMPONENT_KEYS.map((k) => weights[k])
  if (vals.some((v) => typeof v !== 'number' || !Number.isFinite(v) || v < 0)) return false
  const sum = vals.reduce((a, b) => a + b, 0)
  return Math.abs(sum - 100) < EPS
}

/** The 0–1.2 band → the 70–120 band: 0 → 70, 1 → 100, 1.2 → 120. */
export function scoreToKpi (score: number): number {
  const s = Math.min(KPI_SCORE_MAX, Math.max(0, score))
  const kpi = s <= 1 ? KPI_MIN + s * (100 - KPI_MIN) : 100 + ((s - 1) / (KPI_SCORE_MAX - 1)) * (KPI_MAX - 100)
  return Math.round(clampKpi(kpi) * 10) / 10
}

/** A 70–120 assessment → 0–1 (70 → 0, 100 → 1; above 100 is kept, up to 1.2 at 120). */
function assessmentToScore (kpi: number): number {
  const k = clampKpi(kpi)
  return k <= 100 ? (k - KPI_MIN) / (100 - KPI_MIN) : 1 + ((k - 100) / (KPI_MAX - 100)) * (KPI_SCORE_MAX - 1)
}

function clamp01 (n: number): number {
  return Math.min(1, Math.max(0, n))
}

function pct (n: number): string {
  return `${Math.round(n * 100)}%`
}

type Raw = { value: number | null, reason: string }

function rawComponents (m: QuarterMetrics): Record<KpiComponentKey, Raw> {
  const planned = Math.max(0, m.planned)
  const done = Math.max(0, m.done)
  const onTime = Math.max(0, m.onTime)
  const reopened = Math.max(0, m.reopened)

  // Delivery: done / planned, capped at 1.2 so over-delivery lifts the score
  // but not without limit. With nothing planned there is nothing to measure.
  const delivery: Raw =
    planned > 0
      ? { value: Math.min(KPI_SCORE_MAX, done / planned), reason: `Reja ${done}/${planned} bajarildi` }
      : { value: null, reason: 'Reja yo‘q' }

  // Timeliness: the share of finished work that met its due date.
  const timeliness: Raw =
    done > 0
      ? { value: clamp01(Math.min(onTime, done) / done), reason: `${pct(Math.min(onTime, done) / done)} muddatida` }
      : { value: null, reason: 'Bajarilgan ish yo‘q' }

  // Quality: 1 − reopened / done — each reopened issue is rework.
  const quality: Raw =
    done > 0
      ? { value: clamp01(1 - Math.min(reopened, done) / done), reason: `${reopened} ta qaytarilgan` }
      : { value: null, reason: 'Bajarilgan ish yo‘q' }

  // Utilisation: logged / capacity, clamped to 1 (overtime is not a merit).
  const utilization: Raw =
    m.loggedHours !== undefined && m.capacityHours > 0
      ? {
          value: clamp01(Math.max(0, m.loggedHours) / m.capacityHours),
          reason: `${pct(clamp01(Math.max(0, m.loggedHours) / m.capacityHours))} sig‘im ishlatildi`
        }
      : { value: null, reason: 'Soatlar hisoblanmagan' }

  // Assessment: the average of the assessments that were given, on the band.
  const given = [m.selfAssessment, m.leadAssessment].filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  const assessment: Raw =
    given.length > 0
      ? {
          value: given.reduce((a, v) => a + assessmentToScore(v), 0) / given.length,
          reason: `Baho ${given.map((v) => clampKpi(v)).join('/')}`
        }
      : { value: null, reason: 'Baho berilmagan' }

  return { delivery, timeliness, quality, utilization, assessment }
}

/**
 * The suggestion for one quarter. Pure and deterministic: the same metrics
 * and weights always give the same number and the same explanation.
 */
export function suggestKpi (metrics: QuarterMetrics, weights: KpiWeights = DEFAULT_KPI_WEIGHTS): KpiSuggestion {
  if (!kpiWeightsAreValid(weights)) {
    throw new Error('KPI weights must be non-negative and add up to 100')
  }
  const raw = rawComponents(metrics)
  const present = KPI_COMPONENT_KEYS.filter((k) => raw[k].value !== null && weights[k] > 0)
  const presentWeight = present.reduce((a, k) => a + weights[k], 0)

  const components: KpiComponent[] = KPI_COMPONENT_KEYS.map((key) => {
    const { value, reason } = raw[key]
    // A missing component gives its weight to the others, in proportion.
    const weight = value !== null && presentWeight > 0 ? (weights[key] / presentWeight) * 100 : 0
    const points = value !== null ? (weight / 100) * value : 0
    const note =
      value === null && weights[key] > 0 ? `${reason} — vazni boshqalarga taqsimlandi` : reason
    return { key, weight: Math.round(weight * 100) / 100, value, points: Math.round(points * 10000) / 10000, reason: note }
  })

  const score = present.length > 0 ? Math.round(components.reduce((a, c) => a + c.points, 0) * 10000) / 10000 : 1
  const kpi = scoreToKpi(score)
  const confident = present.length >= 2

  const said = components.filter((c) => c.value !== null).map((c) => c.reason)
  const basis =
    present.length === 0
      ? `Chorak bo‘yicha ma’lumot yo‘q; tavsiya ${kpi} (standart)`
      : `${said.join(', ')}; tavsiya ${kpi}${confident ? '' : ' (ma’lumot kam)'}`

  return { kpi, score, components, basis, confident }
}

/**
 * The score that counts: the one typed by hand wins, else the suggestion,
 * else the company default of 100 (an unscored quarter is on target).
 */
export function finalKpi (suggested: number | undefined, manual: number | undefined): number {
  if (typeof manual === 'number' && Number.isFinite(manual)) return clampKpi(manual)
  if (typeof suggested === 'number' && Number.isFinite(suggested)) return clampKpi(suggested)
  return 100
}
